import { useReturnPath } from '@lib/hooks/useReturnPath';
import { SOCIAL_CONNECTORS } from '@lib/social/scheduling/platforms';
import type {
  SocialConnector,
  SocialConnectSource,
  SocialPlatform,
} from '@lib/social/scheduling/types';
import dialogCss from '@lib/style/dialog.module.scss';
import { AddRounded } from '@mui/icons-material';
import {
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { useModalRegistration } from '@stores/ModalStore';
import { socialConnectionsStore } from '@stores/SocialConnections';
import { useState } from 'react';
import css from './ConnectAccountModal.module.scss';

/** What connecting is for, above the connectors here and above the button that opens this in Settings. */
export const CONNECT_ACCOUNT_INTRO =
  'Connect a social account to publish your moments to it. More platforms are coming soon.';

function ConnectorRow({
  connector,
  leaving,
  onPick,
}: {
  connector: SocialConnector;
  /** The platform a pick already started for, if any: every row waits while the browser leaves. */
  leaving: SocialPlatform | null;
  onPick: (platform: SocialPlatform) => void;
}) {
  const picked = leaving === connector.platform;
  return (
    <ButtonBase
      className={css.row}
      disabled={!connector.available || leaving !== null}
      onClick={connector.available ? () => onPick(connector.platform) : undefined}
    >
      <span className={css.icon}>{connector.icon}</span>
      <span className={css.label}>{connector.label}</span>
      {!connector.available ? (
        <span className={css.comingSoonPill}>Coming soon</span>
      ) : picked ? (
        <CircularProgress className={css.trailing} size={18} color="inherit" />
      ) : (
        <AddRounded className={css.trailing} />
      )}
    </ButtonBase>
  );
}

/** The connectors whose consent page needs a word of warning (a pending app review). */
const NOTED_CONNECTORS = SOCIAL_CONNECTORS.filter((connector) => connector.connectNote);

/**
 * "Connect new account": one row per connector, the upcoming ones disabled,
 * and under the list what each noted platform's consent page does before the
 * user is back. Picking a row leaves for the platform and comes back to the
 * page that opened the modal, so the same modal serves Settings, the
 * schedule and the compose page. One pick per open: the rows lock and the
 * picked one spins while the browser leaves, so a slow redirect cannot start
 * a second connect.
 */
export function ConnectAccountModal({
  open,
  onClose,
  source,
}: {
  open: boolean;
  onClose: () => void;
  source: SocialConnectSource;
}) {
  const returnPath = useReturnPath();
  const [leaving, setLeaving] = useState<SocialPlatform | null>(null);
  useModalRegistration('connect-social-account', open);
  const pick = (platform: SocialPlatform) => {
    setLeaving(platform);
    socialConnectionsStore.connect(platform, source, returnPath);
  };
  const close = () => {
    // Closed while the browser is still leaving: the next open starts clean.
    setLeaving(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} className={dialogCss.dialog}>
      <DialogTitle className={dialogCss.dialogTitle}>Connect new account</DialogTitle>
      <DialogContent className={dialogCss.dialogContent}>
        <p className={css.intro}>{CONNECT_ACCOUNT_INTRO}</p>
        <div className={css.list}>
          {SOCIAL_CONNECTORS.map((connector) => (
            <ConnectorRow
              key={connector.platform}
              connector={connector}
              leaving={leaving}
              onPick={pick}
            />
          ))}
        </div>
        {NOTED_CONNECTORS.length > 0 && (
          <ul className={css.notes}>
            {NOTED_CONNECTORS.map((connector) => (
              <li key={connector.platform}>
                <strong>{connector.label}:</strong> {connector.connectNote}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
      <DialogActions className={dialogCss.dialogActions}>
        <ActionButton variant="secondary" onClick={close}>
          Close
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}
